import React, { useState, startTransition, useEffect } from 'react';
import { Tree, Space, Dropdown } from 'antd';
import httpInjectorService from 'services/http-injector.service';
import {
  FolderOutlined,
  FileTextOutlined,
  TeamOutlined,
  AppstoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  ShareAltOutlined,
  FileOutlined,
  ProfileOutlined,
  StarOutlined,
} from '@ant-design/icons';
import './Sidebar.css';
import { toast } from 'react-toastify';
import { useSpace } from '../../hooks/useSpace';
import { useSpaceContent } from '../../hooks/useSpaceContent';
import TemplateSection from '../templatesComponents/TemplateSection';
import { useNavigate, useParams } from 'react-router-dom';
import SpaceTree from './SpaceTree';
import { useQueryClient } from '@tanstack/react-query';

export default function Sidebar({
  setModal,
  selectedTab,
  setSelectedTab,
  spaceid,
  getSpaces,
}) {
  const { data: activeSpace } = useSpace(spaceid);
  const { data: spaceContent } = useSpaceContent(spaceid);

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [selectedDuplicateNode, setSelectedDuplicateNode] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleCreate = async (parentId, type, title) => {
    const payload = {
      space_id: spaceid,
      parent_id: parentId,
      type,
      title,
    };

    try {
      let response;

      if (type === 'page') {
        response = await httpInjectorService.createPageDraft(payload);
      }

      if (type === 'folder') {
        response = await httpInjectorService.createFolder(payload);
      }

      if(type === 'whiteboard'){
        response = await httpInjectorService.createWhiteBoard(payload)
      }

       if(type === 'database'){
        response = await httpInjectorService.createDatabase(payload)
      }

      if (response?.status === 'success') {
        toast.success(response?.message || 'Created successfully');
      } else {
        toast.error(response?.message || 'Something went wrong');
      }

      queryClient.invalidateQueries(['spaceContent', spaceid]);
    } catch (error) {
      console.error(error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';

      toast.error(errorMessage);
    }
  };

  const handleMove = async ({ dragId, dropId }) => {
    const response = await httpInjectorService.movePageOrFolders({
      space_id: Number(spaceid),
      item_id: dragId,
      parent_id: dropId,
    });

    if (response?.status === 'error') {
      toast.error(response.message || 'Item cannot move');
      return;
    }

    toast.success(response.message || 'Item moved succesfully');
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleDelete = async ({ item_id, item_type, status_type }) => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id,
      status_type,
      nested_item: 'yes',
      type: item_type,
    });

    if (response?.status === 'error') {
      toast.error(response.message || 'Item delete failed');
      return;
    }

    toast.success(response.message || 'Item deleted succesfully');
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const deleteDraftPage = async (item_id) => {
    try {
      const response = await httpInjectorService.deleteDraftPage(
        spaceid,
        item_id,
      );
      if (response?.status === 'error') {
        toast.error(response.message || 'Draft delete failed');
        return;
      }

      toast.success(response.message || 'Draft deleted succesfully');
      queryClient.invalidateQueries(['spaceContent', spaceid]);
    } catch (error) {
      console.error('Delete draft failed', error);
    }
  };

  const handleArchive = async ({ item_id, item_type, status_type }) => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id,
      status_type,
      nested_item: 'yes',
      type: item_type,
    });

    if (response?.status === 'error') {
      toast.error(response.message || 'Item archived failed');
      return;
    }

    toast.success(response.message || 'Item Archived succesfully');
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };
  

  const handleRename = async ({ item_id, title, item_type }) => {
    const response = await httpInjectorService.renameContent({
      space_id: Number(spaceid),
      item_id,
      title,
      item_type,
    });

    if (response?.status === 'error') {
      toast.error(response.message || 'Item rename failed');
      return;
    }

    toast.success(response.message || 'Item renamed succesfully');
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleDuplicate = (node) => {
    setSelectedDuplicateNode(node);
    setDuplicateModalOpen(true);
  };

  const handleDuplicateConfirm = async ({
    original_id,
    target_space_id,
    parent_id,
    prefix,
    is_child,
  }) => {
    const response = await httpInjectorService.duplicateContent({
      space_id: Number(target_space_id),
      item_id: original_id,
      parent_id: parent_id || null,
      startname: prefix,
      is_child: is_child ? 'yes' : 'no',
    });

    if (response?.status === 'error') {
      toast.error(response.message || 'Item duplicate failed');
      return;
    }

    toast.success(response.message || 'Item duplicated');
    queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
  };

  const menu = [
  {
    section: 'Space',
    items: [
      {
        id: 'all-spaces',
        label: 'All Spaces',
        icon: <AppstoreOutlined />,
        tour: 'space-sidebar',
      },
      {
        id: 'templates',
        label: 'Templates',
        icon: <ProfileOutlined />,
        tour: 'space-templates',
      },
    ],
  },
];

  return (
    <div className="sidebar">
      <div className="sidebar-content">
        {menu.map((section) => (
          <div key={section.section} className="sidebar-section">
            <div className="sidebar-title" >
              {section.section}
              {section.section === 'Space' && (
                <PlusOutlined
                  data-tour="create-space-btn"
                  className="add-icon"
                  onClick={() => setModal(true)}
                 
                />
              )}
            </div>

            {section.items.map((item) => (
            <div
  key={item.id}
  data-tour={item.tour}
  className={`sidebar-item ${
    selectedTab === item.id ? 'active' : ''
  }`}
  onClick={() => {
    startTransition(() => {
      setSelectedTab(item.id);

      if (item.id === 'all-spaces') {
        navigate('*');
      }

      if (item.id === 'templates') {
        navigate('template');
      }
    });
  }}
>
  <span className="sidebar-icon">{item.icon}</span>
  <span className="sidebar-label">{item.label}</span>
</div>
            ))}
          </div>
        ))}
        {activeSpace && spaceContent && (
          <SpaceTree
            spaceid={spaceid}
            space={activeSpace}
            treeData={spaceContent || []}
            onCreate={handleCreate}
            onMove={handleMove}
            onDelete={handleDelete}
            onRename={handleRename}
            onDuplicate={handleDuplicate}
            onArchive={handleArchive}
            duplicateModalOpen={duplicateModalOpen}
            setDuplicateModalOpen={setDuplicateModalOpen}
            handleDuplicateConfirm={handleDuplicateConfirm}
            selectedDuplicateNode={selectedDuplicateNode}
            getSpaces={getSpaces}
            deleteDraftPage={deleteDraftPage}
          />
        )}
      </div>
    </div>
  );
}
